// src/pages/providers/2_dashboard/components/ProviderAccountsTable.js
import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { ChevronUpIcon, ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/solid';
import { TrashIcon, UserIcon, CalendarIcon, DocumentDuplicateIcon, PencilIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { formatDateShort } from '../../../../utils/timestampUtils';
import { PATHWAY_SPECIALISM_LABELS, getEffectiveSpecialisms } from '../../../../utils/pathwaySpecialismUtils';
import StatusMessage from '../../../../components/StatusMessage';
import ClinicianRemoveForm from './ClinicianRemoveForm';
import ProviderEditUserForm from './ProviderEditUserForm';
import useSessionState from '../../../../hooks/useSessionState';
import { DASHBOARD_KEYS } from '../../../../utils/dashboardViewState';
import { matchesStaffFilters, hasActiveStaffFilters } from '../../../../utils/personFilterUtils';
import { DEFAULT_STAFF_FILTERS } from './StaffFilterControls';
import CondensedPagination from '../../../../components/CondensedPagination';
import { getUiUserType } from '../../../../utils/accountTypeUtils';
import DashboardPersonName from './DashboardPersonName';
import { emitStaffEditOpened } from '../../../../analytics/caseloadNavEvents';

function ProviderAccountsTable({ activeFilters = DEFAULT_STAFF_FILTERS }) {
    const { userData, providerAccountsData } = useContext(GlobalStateContext);
    // Sort order is session-persisted (own key) so the provider accounts table
    // keeps its sorting after navigating away and back, on par with the patient
    // tables. Pagination is intentionally not persisted.
    const [sortConfig, setSortConfig] = useSessionState(
        DASHBOARD_KEYS.providerAccountsSort,
        () => ({ key: null, direction: 'ascending' })
    );
    const [removingClinician, setRemovingClinician] = useState(null);
    const [editingClinician, setEditingClinician] = useState(null);
    const [clickedButton, setClickedButton] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const providersPerPage = 10;

    // ─── Org assessment modules (single source of truth for pathway editing) ──
    //
    // The edit dialog lets admins toggle which assessment pathways a clinician
    // can be assigned to (e.g. Adult ADHD, Adult Autism). A pathway should only
    // be selectable if the organisation actually runs that assessment module —
    // most orgs only have Adult ADHD enabled, so Adult Autism must be greyed out
    // for them. The authoritative list lives on the org document
    // (`organisations/{orgId}.availableAssessmentModules`) and is exposed by the
    // `getOrgConfig` callable (same source the setup form and dashboard use).
    //
    // We fetch it ONCE here, at the table level, rather than inside the modal so
    // that: (a) the value is already resolved by the time any Edit dialog opens
    // (no per-open network round-trip, no janky "pop-in" of options), and (b) it
    // is shared across every row's edit dialog. The default is the safe minimal
    // set (`['adultAdhd']`), which means extra pathways start disabled/greyed and
    // only become clickable once the fetch confirms the org supports them — never
    // the other way around, so options don't flip from enabled to disabled.
    const [availableOrgModules, setAvailableOrgModules] = useState(['adultAdhd']);

    useEffect(() => {
        // Only Org. Admins / Support can call getOrgConfig and are the only roles
        // that can edit staff pathways; skip the call entirely for everyone else.
        const isAdminOrSupport =
            userData?.userType === 'Org. Admin' || userData?.userType === 'Support';
        if (!isAdminOrSupport) return;

        let cancelled = false;
        const fetchOrgModules = async () => {
            try {
                const getOrgConfigFn = httpsCallable(functions, 'getOrgConfig');
                const result = await getOrgConfigFn();
                if (cancelled) return;
                const modules = result?.data?.availableAssessmentModules;
                if (Array.isArray(modules) && modules.length > 0) {
                    setAvailableOrgModules(modules);
                }
            } catch (_) {
                // Non-fatal: fall back to the safe default (Adult ADHD only). This
                // never blocks the admin from editing other details; it simply
                // keeps non-default pathways greyed out if the config can't load.
            }
        };
        fetchOrgModules();
        return () => { cancelled = true; };
    }, [userData?.userType]);

    // Staff‑management permissions:
    // - Org. Admins and Support staff can manage (e.g. delete/archive) provider accounts.
    // - Clinicians can see the list via other contexts but do not get destructive controls.
    const canManageStaffAccounts =
        userData?.userType === 'Org. Admin' || userData?.userType === 'Support';

    // Name click and the pencil share this opener so they cannot drift on
    // permissions, loading or errors — both just set the same piece of state.
    const openEditDetails = (provider, trigger) => {
        emitStaffEditOpened({ staffId: provider.clinicianId, trigger });
        setEditingClinician(provider);
    };

    // Business rule for who can delete which provider accounts from the UI:
    // - Support users: can delete Clinician and Support accounts in their org, but never Org. Admins
    //   and never themselves.
    // - Org. Admins: can delete any provider account in their org except themselves.
    const canDeleteProvider = useCallback(
        (currentUser, targetProvider) => {
            if (!currentUser || !targetProvider) return false;
            // Never allow self-delete from the UI; the backend enforces this too.
            if (currentUser.clinicianId === targetProvider.clinicianId) return false;

            if (currentUser.userType === 'Support') {
                return (
                    targetProvider.userType === 'Clinician' ||
                    targetProvider.userType === 'Support'
                );
            }

            if (currentUser.userType === 'Org. Admin') {
                // Org. Admins can delete any other provider account (including other Org. Admins).
                return true;
            }

            // Clinicians and all other roles get no destructive controls here.
            return false;
        },
        []
    );

    // Restrict to this org, then apply the staff search/filter (name + email
    // substrings, date-added range). Filtering is a pure derivation over the
    // already-loaded org staff list — no extra Firestore reads — mirroring how
    // the patient tables filter patientsData.
    const filteredProviders = useMemo(() => {
        return providerAccountsData
            .filter(provider => provider.organisation === userData.organisation)
            .filter(provider => matchesStaffFilters(provider, activeFilters));
    }, [providerAccountsData, userData.organisation, activeFilters]);

    // Reset pagination when the filter changes so we never strand the user on a
    // now-empty page. Pagination itself is intentionally not persisted.
    useEffect(() => {
        setCurrentPage(1);
    }, [activeFilters]);

    // Define helper functions before they're used in sorting
    const getRegistrationNumber = useCallback((provider) => {
        const customFields = provider.customFields || {};
        const registrationBody = customFields.registrationBody;
        const registrationNumber = customFields.registrationNumber;
        
        if (registrationBody && registrationNumber) {
            return `${registrationBody}: ${registrationNumber}`;
        }
        return '-';
    }, []);

    const getUserRole = useCallback((provider) => {
        const customFields = provider.customFields || {};
        return customFields.role || '-';
    }, []);

    const sortedProviders = useMemo(() => {
        let sortableProviders = [...filteredProviders];
        if (sortConfig.key) {
            sortableProviders.sort((a, b) => {
                if (sortConfig.key === 'name') {
                    const aName = `${a.firstName} ${a.lastName}`;
                    const bName = `${b.firstName} ${b.lastName}`;
                    return sortConfig.direction === 'ascending'
                        ? aName.localeCompare(bName)
                        : bName.localeCompare(aName);
                }
                
                if (sortConfig.key === 'createdDate') {
                    const aValue = a.createdAt;
                    const bValue = b.createdAt;
                    
                    // Handle Firestore Timestamps and missing dates
                    let aDate, bDate;
                    
                    if (aValue && aValue.toDate) {
                        aDate = aValue.toDate();
                    } else if (aValue) {
                        aDate = new Date(aValue);
                    } else {
                        aDate = new Date(0); // Epoch for missing dates
                    }
                    
                    if (bValue && bValue.toDate) {
                        bDate = bValue.toDate();
                    } else if (bValue) {
                        bDate = new Date(bValue);
                    } else {
                        bDate = new Date(0); // Epoch for missing dates
                    }
                    
                    return sortConfig.direction === 'ascending'
                        ? aDate - bDate
                        : bDate - aDate;
                }
                
                if (sortConfig.key === 'registration') {
                    const aValue = getRegistrationNumber(a);
                    const bValue = getRegistrationNumber(b);
                    return sortConfig.direction === 'ascending'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }
                
                if (sortConfig.key === 'role') {
                    const aValue = getUserRole(a);
                    const bValue = getUserRole(b);
                    return sortConfig.direction === 'ascending'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }
                
                // For the userType column, sort by the derived UI label so that
                // Clinical/Non-Clinical Org. Admins sort distinctly rather than
                // all grouping under the raw "Org. Admin" string.
                if (sortConfig.key === 'userType') {
                    const aValue = getUiUserType(a);
                    const bValue = getUiUserType(b);
                    return sortConfig.direction === 'ascending'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                const aValue = a[sortConfig.key] || '';
                const bValue = b[sortConfig.key] || '';
                return sortConfig.direction === 'ascending'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            });
        }
        return sortableProviders;
    }, [filteredProviders, sortConfig, getRegistrationNumber, getUserRole]);

    // Clamp currentPage when the filtered list shrinks (e.g. after removing a
    // provider). Without this, the user could see an empty final page.
    useEffect(() => {
        const maxPage = Math.ceil(sortedProviders.length / providersPerPage);
        if (maxPage > 0 && currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [sortedProviders.length, currentPage]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return null;
        }
        // White to stay visible against the dark (bg-[#43213e]) header — matches
        // the column labels and PatientTable's sort chevrons. Previously slate-700,
        // which was effectively invisible on the dark header.
        return sortConfig.direction === 'ascending'
            ? <ChevronUpIcon className="w-4 h-4 ml-1 text-white" />
            : <ChevronDownIcon className="w-4 h-4 ml-1 text-white" />;
    };

    const copyUserIdToClipboard = async (userId, clinicianId) => {
        try {
            await navigator.clipboard.writeText(userId);
            
            // Show blue button state temporarily
            setClickedButton(clinicianId);
            setTimeout(() => setClickedButton(null), 300); // Blue for 300ms
            
            // Show toast notification
            toast.success(`User ID ${userId} has been copied to clipboard`);
            
        } catch (err) {
            console.error('Failed to copy user ID:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = userId;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            // Show blue button state temporarily
            setClickedButton(clinicianId);
            setTimeout(() => setClickedButton(null), 300);
            
            // Show toast notification
            toast.success(`User ID ${userId} has been copied to clipboard`);
        }
    };

    const handleClinicianDeleted = () => {
        // This function can be used for additional cleanup or UI updates after deletion
        console.log('Clinician deleted successfully');
    };

    // Pagination logic
    const indexOfLastProvider = currentPage * providersPerPage;
    const indexOfFirstProvider = indexOfLastProvider - providersPerPage;
    const currentProviders = sortedProviders.slice(indexOfFirstProvider, indexOfLastProvider);
    
    const totalPages = Math.ceil(sortedProviders.length / providersPerPage);

    if (sortedProviders.length === 0) {
        return (
            <StatusMessage
                color="gray"
                icon={<InformationCircleIcon className="w-5 h-5 mr-2" />}
            >
                {hasActiveStaffFilters(activeFilters)
                    ? 'No staff match your filters. Try adjusting or clearing them.'
                    : 'No provider accounts found for your organisation.'}
            </StatusMessage>
        );
    }

    return (
        <div className="w-full space-y-4">
            <div className="relative flex flex-col w-full h-full overflow-auto text-gray-700 bg-white border border-slate-200 shadow-sm rounded-lg isolate">
                <table className="w-full text-left table-auto min-w-max">
                    <thead>
                        <tr>
                            <th 
                                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 w-[250px] sticky left-0 z-20 after:absolute after:top-0 after:left-full after:h-full after:w-4 after:bg-gradient-to-r after:from-black/[0.10] after:to-transparent after:pointer-events-none after:content-['']"
                                onClick={() => requestSort('name')}
                            >
                                <div className="flex items-center">
                                    <p className="text-xs font-medium text-white">
                                        Name
                                    </p>
                                    {getSortIcon('name')}
                                </div>
                            </th>
                            <th 
                                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200"
                                onClick={() => requestSort('userType')}
                            >
                                <div className="flex items-center">
                                    <p className="text-xs font-medium text-white">
                                        User Type
                                    </p>
                                    {getSortIcon('userType')}
                                </div>
                            </th>
                            <th className="p-3 border-b border-[#43213e] bg-[#43213e]">
                                <div className="flex items-center">
                                    <p className="text-xs font-medium text-white">
                                        Pathways
                                    </p>
                                </div>
                            </th>
                            <th 
                                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200"
                                onClick={() => requestSort('createdDate')}
                            >
                                <div className="flex items-center">
                                    <p className="text-xs font-medium text-white">
                                        Date Added
                                    </p>
                                    {getSortIcon('createdDate')}
                                </div>
                            </th>
                            <th 
                                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200"
                                onClick={() => requestSort('registration')}
                            >
                                <div className="flex items-center">
                                    <p className="text-xs font-medium text-white">
                                        Registration
                                    </p>
                                    {getSortIcon('registration')}
                                </div>
                            </th>
                            <th 
                                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200"
                                onClick={() => requestSort('role')}
                            >
                                <div className="flex items-center">
                                    <p className="text-xs font-medium text-white">
                                        Role
                                    </p>
                                    {getSortIcon('role')}
                                </div>
                            </th>
                            <th className="p-3 border-b border-[#43213e] bg-[#43213e]">
                                <p className="text-xs font-medium text-white">
                                    Actions
                                </p>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentProviders.map((provider) => (
                            <tr
                                key={provider.clinicianId}
                                className="group hover:bg-slate-50 border-b border-slate-200"
                            >
                                <td className="px-3 py-2 w-[250px] sticky left-0 z-10 bg-white group-hover:bg-slate-50 after:absolute after:top-0 after:left-full after:h-full after:w-4 after:bg-gradient-to-r after:from-black/[0.07] after:to-transparent after:pointer-events-none after:content-['']">
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
                                            <UserIcon className="h-4 w-4 text-violet-800" />
                                        </div>
                                        <div>
                                            {canManageStaffAccounts ? (
                                                <DashboardPersonName
                                                    ariaLabel={`Edit details for ${
                                                        `${provider.title || ''} ${provider.firstName || ''} ${provider.lastName || ''}`.trim()
                                                        || 'this staff member'
                                                    }`}
                                                    onClick={() => openEditDetails(provider, 'name')}
                                                >
                                                    {provider.title} {provider.firstName} {provider.lastName}
                                                </DashboardPersonName>
                                            ) : (
                                                <p className="block font-semibold text-xs text-slate-800">
                                                    {provider.title} {provider.firstName} {provider.lastName}
                                                </p>
                                            )}
                                            <p className="text-xs text-slate-500">
                                                {provider.email}
                                            </p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-1.5">
                                        <p className="text-xs text-slate-500">{getUiUserType(provider)}</p>
                                        {provider.accountDisabled === true && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                                                Disabled
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    {/* Pathway badges — shows which assessment pathways this
                                        clinician can be assigned to. Non-assignable users (Support
                                        and non-clinical Org. Admins) cannot hold patient assignments,
                                        so we show a dash. Badge colours match the PatientTable
                                        pathway column: teal for Autism, blue for ADHD. */}
                                    {provider.userType === 'Support' || provider.isAssignableClinician === false ? (
                                        <p className="text-xs text-slate-500">-</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            {getEffectiveSpecialisms(provider).map((specialism) => (
                                                <span
                                                    key={specialism}
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        specialism === 'adultAutism'
                                                            ? 'bg-teal-100 text-teal-800'
                                                            : specialism === 'cypAdhd'
                                                                ? 'bg-purple-100 text-purple-800'
                                                                : 'bg-blue-100 text-blue-800'
                                                    }`}
                                                >
                                                    {PATHWAY_SPECIALISM_LABELS[specialism] || specialism}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="flex items-center space-x-2">
                                        <CalendarIcon className="h-4 w-4 text-slate-400" />
                                        <p className="text-xs text-slate-500">{formatDateShort(provider.createdAt, 'N/A')}</p>
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <p className="text-xs text-slate-500">{getRegistrationNumber(provider)}</p>
                                </td>
                                <td className="px-3 py-2">
                                    <p className="text-xs text-slate-500">{getUserRole(provider)}</p>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => copyUserIdToClipboard(provider.clinicianId, provider.clinicianId)}
                                            className={`p-2 transition-all duration-200 border rounded-lg ${
                                                clickedButton === provider.clinicianId
                                                    ? 'text-white bg-blue-600 border-blue-600'
                                                    : 'text-blue-600 bg-white border-blue-200 hover:bg-blue-50'
                                            }`}
                                            aria-label="Copy user ID"
                                            title="Copy User ID"
                                            data-tooltip-id={`prov-action-copy-${provider.clinicianId}`}
                                        >
                                            <DocumentDuplicateIcon className="w-4 h-4" />
                                        </button>
                                        <Tooltip
                                            id={`prov-action-copy-${provider.clinicianId}`}
                                            place="top"
                                            style={{
                                                maxWidth: '250px',
                                                whiteSpace: 'normal',
                                                backgroundColor: 'rgba(34, 34, 34, 1)',
                                                borderRadius: '10px',
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                fontSize: '0.875rem'
                                            }}
                                            content="Copy ID"
                                        />
                                        {/* Edit button — visible to Org. Admin and Support users */}
                                        {canManageStaffAccounts && (
                                            <>
                                                <button
                                                    onClick={() => openEditDetails(provider, 'action_icon')}
                                                    className="p-2 text-violet-600 bg-white border border-violet-200 rounded-lg hover:bg-violet-50 transition-colors duration-200"
                                                    aria-label="Edit user details"
                                                    data-tooltip-id={`prov-action-edit-${provider.clinicianId}`}
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                                <Tooltip
                                                    id={`prov-action-edit-${provider.clinicianId}`}
                                                    place="top"
                                                    style={{
                                                        maxWidth: '250px',
                                                        whiteSpace: 'normal',
                                                        backgroundColor: 'rgba(34, 34, 34, 1)',
                                                        borderRadius: '10px',
                                                        color: '#fff',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.875rem'
                                                    }}
                                                    content="Edit Details"
                                                />
                                            </>
                                        )}
                                        {/*
                                          Deletion controls:
                                          - Available to Org. Admin and Support users so front‑of‑house staff can
                                            offboard colleagues.
                                          - Support users are restricted to deleting Clinician / Support accounts only.
                                          - Org. Admins may delete any provider account except their own.
                                          - The backend Cloud Function re-enforces these rules as a safety net.
                                        */}
                                        {canManageStaffAccounts && canDeleteProvider(userData, provider) && (
                                            <>
                                                <button
                                                    onClick={() => setRemovingClinician(provider)}
                                                    className="p-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors duration-200"
                                                    aria-label="Delete clinician"
                                                    data-tooltip-id={`prov-action-delete-${provider.clinicianId}`}
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                                <Tooltip
                                                    id={`prov-action-delete-${provider.clinicianId}`}
                                                    place="top"
                                                    style={{
                                                        maxWidth: '250px',
                                                        whiteSpace: 'normal',
                                                        backgroundColor: 'rgba(34, 34, 34, 1)',
                                                        borderRadius: '10px',
                                                        color: '#fff',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.875rem'
                                                    }}
                                                    content="Remove User"
                                                />
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                
                {/* Pagination */}
                <CondensedPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={sortedProviders.length}
                    pageSize={providersPerPage}
                    onPageChange={setCurrentPage}
                />
            </div>

            {/* Clinician Remove Dialog — rendered unconditionally so Radix Presence
               can animate the exit transition when `open` flips to false. */}
            <ClinicianRemoveForm
                isOpen={!!removingClinician}
                onClose={() => setRemovingClinician(null)}
                clinician={removingClinician}
                onSuccess={handleClinicianDeleted}
            />

            {/* Provider Edit User Details Dialog */}
            <ProviderEditUserForm
                isOpen={!!editingClinician}
                onClose={() => setEditingClinician(null)}
                clinician={editingClinician}
                availableOrgModules={availableOrgModules}
            />
        </div>
    );
}

export default ProviderAccountsTable;